import React, { useEffect, useState } from 'react';
import { CollectionFilterState } from '../../state';
import { observer } from 'mobx-react-lite';
import { CollectionState, Filter, FiltersMap, KeyedItem } from '@wix/bex-core';
import {
  AutoComplete,
  AutoCompleteProps,
  DropdownLayoutValueOption,
} from '@wix/design-system';
import { ListItem, useBuildGroupedOptions, useBuildOption } from '../../hooks';
import { AutoCompleteFilterState } from './AutoCompleteFilterState';
import { SingleSelectFilterBaseProps } from '../SingleSelectFilterBase';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { useFilterToolbarState } from '../../providers';
import { FilterProps } from '../../model';

export type KeyedItemOption<T> = DropdownLayoutValueOption & {
  keyedItem: KeyedItem<T>;
};

export interface AutoCompleteFilterProps<V, F extends FiltersMap>
  extends Omit<AutoCompleteProps, 'options'>,
    FilterProps<V[]> {
  /**
   * [ArrayFilter](/pages/cairo/?path=/story/common-factories--arrayfilter) state object
   */
  filter: Filter<V[]>;
  /**
   * A function that returns a [ListItemSelectProps](https://www.docs.wixdesignsystem.com/?path=/story/components-form-dropdownlistitems--listitemselect) (Optional)
   */
  renderItem?: (item: V, index: number) => ListItem;
  /**
   * A function that returns  [ListItemSectionProps](https://www.docs.wixdesignsystem.com/?path=/story/components-form-dropdownlistitems--listitemsection) <br/>
   * According to make it works you need return `sectionId` in `renderItem` method. <br/>
   * ⚠️ Server should return section items sorted by sections in strict order. Otherwise, when using infinite scrolling the behavior of the sections will be unexpected. <br/>
   * (Optional)
   */
  renderSection?: SingleSelectFilterBaseProps<V>['renderSection'];
  /**
   * A collection state object to fetch the dropdown items.<br>
   * Typically, a collection created using [useFilterCollection](/pages/cairo/?path=/story/common-hooks--usefiltercollection) hook.
   */
  collection: CollectionState<V, F>;
}

function _AutoCompleteFilter<V, F extends FiltersMap>(
  props: AutoCompleteFilterProps<V, F>,
) {
  const {
    filter,
    collection,
    renderItem,
    renderSection,
    placeholder,
    onSelect,
    onClear,
    ...rest
  } = props;

  const container = useWixPatternsContainer();
  const toolbar = useFilterToolbarState();

  const [collectionFilter] = useState(
    () =>
      new CollectionFilterState({
        collection,
        filter,
      }),
  );

  const [state] = useState(
    () =>
      new AutoCompleteFilterState({
        collectionFilter,
        resetOnClose: false,
        container,
        toolbar,
      }),
  );

  const { translate: t } = collection;

  useEffect(() => {
    return state.init();
  }, []);

  const {
    result: { keyedItems },
    fetchedLastPage,
  } = collection;

  const buildOption = useBuildOption({
    filter,
    renderItem,
  });

  const builtOptions = keyedItems.map(buildOption);

  const options = useBuildGroupedOptions({
    items: builtOptions,
    renderSection,
  });

  return (
    <AutoComplete
      dataHook={`collection-filter-${filter.name}`}
      closeOnSelect
      border="round"
      options={options}
      onClose={() => {
        state.dropdown.dropdownSelect.close();
        state.onInputBlur();
      }}
      onBlur={state.onInputBlur}
      clearButton
      onClear={(e) => {
        onClear?.(e);
        state.clearSelectionAndSearch();
      }}
      placeholder={placeholder ?? t('cairo.selectPlaceholder')}
      onSelect={(option, sameOptionWasPicked) => {
        const { keyedItem } = option as KeyedItemOption<V>;
        state.dropdown.dropdownSelect.setSingle(keyedItem);
        onSelect?.(option, sameOptionWasPicked);
        state.searchInputValue = state.collection.itemName(keyedItem.item);
      }}
      infiniteScroll
      value={state.searchInputValue}
      onChange={(e) => {
        state.searchInputValue = e.target.value;
        collection.search(e.target.value);
      }}
      hasMore={!fetchedLastPage}
      loadMore={collection.fetchNextPageIfNeeded}
      {...rest}
    />
  );
}

export const AutoCompleteFilter = observer(_AutoCompleteFilter);
